import { z } from "zod";
import { decimal, trimmed, uuid } from "./common";

export const ORDER_TYPES = ["dine_in", "takeaway", "delivery", "self_service", "pickup"] as const;
export type OrderType = (typeof ORDER_TYPES)[number];
/** Причины отмены/void (G-02, BR-042). «other» требует комментарий. */
export const REASON_CODES = ["guest_mistake", "waiter_mistake", "long_wait", "out_of_stock", "quality", "other"] as const;
export const OPEN_ORDER_STATUSES = ["draft", "open", "confirmed", "cooking", "ready", "served", "paid"] as const;

const qty = decimal.refine((v) => Number(v) > 0 && Number(v) <= 999, { error: "Больше 0" });

export const orderItemInput = z.strictObject({
  /** Клиентский UUID — повтор запроса не создаёт дубль (BR-009). */
  id: uuid.optional(),
  menuItemId: uuid,
  variantId: uuid.nullable().optional(),
  quantity: qty.default("1"),
  modifiers: z.array(z.strictObject({ modifierId: uuid, quantity: z.number().int().min(1).max(20).default(1) })).max(50).default([]),
  comment: trimmed(0, 200).nullable().optional(),
  course: z.number().int().min(1).max(5).default(1),
  seatNumber: z.number().int().min(1).max(50).nullable().optional(),
});
export type OrderItemInput = z.input<typeof orderItemInput>;

export const orderCreateSchema = z
  .strictObject({
    id: uuid.optional(),
    type: z.enum(ORDER_TYPES),
    tableId: uuid.nullable().optional(),
    guestsCount: z.number().int().min(1).max(500).nullable().optional(),
    customerName: trimmed(0, 80).nullable().optional(),
    comment: trimmed(0, 500).nullable().optional(),
    items: z.array(orderItemInput).max(50).default([]),
    clientCreatedAt: z.iso.datetime().optional(),
  })
  .refine((o) => o.type !== "dine_in" || !!o.tableId, { error: "Для заказа в зале выберите стол", path: ["tableId"] });
export type OrderCreateInput = z.input<typeof orderCreateSchema>;

export const orderUpdateSchema = z.strictObject({
  type: z.enum(ORDER_TYPES).optional(),
  tableId: uuid.nullable().optional(),
  guestsCount: z.number().int().min(1).max(500).nullable().optional(),
  customerName: trimmed(0, 80).nullable().optional(),
  comment: trimmed(0, 500).nullable().optional(),
  version: z.number().int().min(1),
});

export const addItemsSchema = z.strictObject({ items: z.array(orderItemInput).min(1).max(50) });

export const updateItemSchema = z.strictObject({
  quantity: qty.optional(),
  comment: trimmed(0, 200).nullable().optional(),
  course: z.number().int().min(1).max(5).optional(),
  seatNumber: z.number().int().min(1).max(50).nullable().optional(),
});

const reason = z
  .strictObject({ reasonCode: z.enum(REASON_CODES), comment: trimmed(0, 200).nullable().optional() })
  .refine((r) => r.reasonCode !== "other" || !!r.comment?.trim(), { error: "Опишите причину", path: ["comment"] });
export const voidItemSchema = reason;
export const cancelOrderSchema = reason;

export const sendToKitchenSchema = z.strictObject({ itemIds: z.array(uuid).max(100).optional() });

export const ordersQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  scope: z.enum(["open", "all"]).default("open"),
  status: z.string().max(200).optional(),
  type: z.enum(ORDER_TYPES).optional(),
  tableId: uuid.optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  q: z.string().trim().max(50).optional(),
});

// ───────── Кухня ─────────
export const KITCHEN_STATION_TYPES = ["hot", "cold", "bar", "coffee", "bakery", "pizza", "grill", "dessert", "packing", "expo"] as const;
export const kitchenStationSchema = z.strictObject({
  branchId: uuid,
  name: trimmed(1, 60),
  type: z.enum(KITCHEN_STATION_TYPES),
  normMinutes: z.number().int().min(1).max(240).default(15),
  simpleMode: z.boolean().default(false),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true),
  /** Склад станции (BR-132, addon.inventory_pro): продажи позиций станции списываются с него; null — основной склад */
  warehouseId: uuid.nullable().optional(),
});
export const kitchenStationUpdateSchema = kitchenStationSchema.omit({ branchId: true }).partial().extend({ version: z.number().int().min(1) });

export const TICKET_STATUSES = ["new", "accepted", "cooking", "ready", "served", "cancelled"] as const;
export const ticketStatusSchema = z.strictObject({ status: z.enum(["accepted", "cooking", "ready", "served", "recall"]), version: z.number().int().min(1) });
export const ticketItemStatusSchema = z.strictObject({ status: z.enum(["cooking", "ready"]) });

/**
 * Бизнес-дата (BR-013): дата в часовом поясе филиала с учётом времени перехода дня.
 * Заказ в 01:30 при переходе 05:00 относится к предыдущему дню.
 */
/** Календарная дата YYYY-MM-DD в часовом поясе (без границы бизнес-дня). */
export const localDate = (timezone: string, at = new Date()) => businessDate(at, timezone, "00:00");

export function businessDate(at: Date, timezone: string, dayCutoff = "05:00"): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(at)
      .map((p) => [p.type, p.value]),
  );
  const [ch, cm] = dayCutoff.split(":").map(Number);
  const local = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
  const beforeCutoff = Number(parts.hour) * 60 + Number(parts.minute) < (ch ?? 0) * 60 + (cm ?? 0);
  return new Date(local - (beforeCutoff ? 86_400_000 : 0)).toISOString().slice(0, 10);
}
